import React, { useState, useEffect } from 'react';
import { useForm } from '../../../context/FormContext';
import FormField from '../../common/FormField/FormField';
import CountrySelect from '../../common/CountrySelect/CountrySelect';
import { validateSection, isSectionComplete } from '../../../utils/validation';
import './CompanyDetails.css';

const CompanyDetails = ({ data, errors, onSave, isLoading, isReadOnly }) => {
  const { actions } = useForm();
  const [formData, setFormData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;

    const newData = { ...formData, [field]: value };
    setFormData(newData);
    setHasChanges(true);

    // Update context immediately for real-time validation
    actions.updateSectionData('companyDetails', newData, 'in_progress');
  };

  const handleSave = () => {
    if (hasChanges) {
      // Validate the section
      const validationErrors = validateSection('companyDetails', formData);
      const isComplete = isSectionComplete('companyDetails', formData);

      // Set validation errors in context
      actions.setSectionErrors('companyDetails', validationErrors);

      // Determine status based on completion
      const status = isComplete ? 'completed' : 'in_progress';

      // Update section data with proper status
      actions.updateSectionData('companyDetails', formData, status);

      // Call the save handler
      onSave(formData);
      setHasChanges(false);
    }
  };

  const handleBusinessAddressToggle = (value) => {
    handleChange('isDifferentBusinessAddress', value);

    // Clear business address fields if not different
    if (!value) {
      const clearedData = {
        ...formData,
        isDifferentBusinessAddress: false,
        businessAddress: '',
        businessCity: '',
        businessPostcode: '',
        businessCountry: ''
      };
      setFormData(clearedData);
      actions.updateSectionData('companyDetails', clearedData, 'in_progress');
    }
  };

  // Auto-save when user stops typing
  // Removed auto-saving - users must manually save sections

  return (
    <div className="company-details">
      <div className="section-intro">
        <p>Please provide your company's basic information and registered address.</p>
      </div>

      <div className="form-grid">
        <FormField
          label="Company name"
          required
          error={errors.companyName}
          className="grid-span-2"
        >
          <input
            type="text"
            className={`form-input ${errors.companyName ? 'error' : ''}`}
            value={formData.companyName || ''}
            onChange={(e) => handleChange('companyName', e.target.value)}
            placeholder="Enter your company name"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Jurisdiction of incorporation"
          required
          error={errors.jurisdiction}
        >
          <input
            type="text"
            className={`form-input ${errors.jurisdiction ? 'error' : ''}`}
            value={formData.jurisdiction || ''}
            onChange={(e) => handleChange('jurisdiction', e.target.value)}
            placeholder="e.g., Delaware, UK, etc."
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Registration number"
          required
          error={errors.registrationNumber}
        >
          <input
            type="text"
            className={`form-input ${errors.registrationNumber ? 'error' : ''}`}
            value={formData.registrationNumber || ''}
            onChange={(e) => handleChange('registrationNumber', e.target.value)}
            placeholder="Company registration number"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Date of incorporation"
          required
          error={errors.incorporationDate}
        >
          <input
            type="date"
            className={`form-input ${errors.incorporationDate ? 'error' : ''}`}
            value={formData.incorporationDate || ''}
            onChange={(e) => handleChange('incorporationDate', e.target.value)}
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Company type"
          required
          error={errors.companyType}
        >
          <input
            type="text"
            className={`form-input ${errors.companyType ? 'error' : ''}`}
            value={formData.companyType || ''}
            onChange={(e) => handleChange('companyType', e.target.value)}
            placeholder="e.g., LLC, Corporation, Ltd"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Tax ID number"
          required
          error={errors.taxId}
        >
          <input
            type="text"
            className={`form-input ${errors.taxId ? 'error' : ''}`}
            value={formData.taxId || ''}
            onChange={(e) => handleChange('taxId', e.target.value)}
            placeholder="Tax identification number"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Business activity/industry"
          required
          error={errors.businessActivity}
          className="grid-span-2"
        >
          <input
            type="text"
            className={`form-input ${errors.businessActivity ? 'error' : ''}`}
            value={formData.businessActivity || ''}
            onChange={(e) => handleChange('businessActivity', e.target.value)}
            placeholder="Describe your main business activity"
            disabled={isReadOnly}
          />
        </FormField>

        <FormField
          label="Website"
          error={errors.website}
          help="Optional - include https://"
          className="grid-span-2"
        >
          <input
            type="url"
            className={`form-input ${errors.website ? 'error' : ''}`}
            value={formData.website || ''}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder="https://example.com"
            disabled={isReadOnly}
          />
        </FormField>
      </div>

      <div className="address-section">
        <h3>Registered Business Address</h3>

        <div className="form-grid">
          <FormField
            label="Address"
            required
            error={errors.registeredAddress}
            className="grid-span-2"
          >
            <input
              type="text"
              className={`form-input ${errors.registeredAddress ? 'error' : ''}`}
              value={formData.registeredAddress || ''}
              onChange={(e) => handleChange('registeredAddress', e.target.value)}
              placeholder="Street address"
              disabled={isReadOnly}
            />
          </FormField>

          <FormField
            label="City"
            required
            error={errors.registeredCity}
          >
            <input
              type="text"
              className={`form-input ${errors.registeredCity ? 'error' : ''}`}
              value={formData.registeredCity || ''}
              onChange={(e) => handleChange('registeredCity', e.target.value)}
              placeholder="City"
              disabled={isReadOnly}
            />
          </FormField>

          <FormField
            label="Postcode"
            required
            error={errors.registeredPostcode}
          >
            <input
              type="text"
              className={`form-input ${errors.registeredPostcode ? 'error' : ''}`}
              value={formData.registeredPostcode || ''}
              onChange={(e) => handleChange('registeredPostcode', e.target.value)}
              placeholder="Postcode"
              disabled={isReadOnly}
            />
          </FormField>

          <FormField
            label="Country"
            required
            error={errors.registeredCountry}
            className="grid-span-2"
          >
            <CountrySelect
              value={formData.registeredCountry || ''}
              onChange={(value) => handleChange('registeredCountry', value)}
              error={!!errors.registeredCountry}
              disabled={isReadOnly}
            />
          </FormField>
        </div>
      </div>

      <div className="business-address-section">
        <FormField
          label="Is your business address different from registered address?"
          className="radio-field"
        >
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="isDifferentBusinessAddress"
                value="true"
                checked={formData.isDifferentBusinessAddress === true}
                onChange={() => handleBusinessAddressToggle(true)}
                disabled={isReadOnly}
              />
              <span>Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="isDifferentBusinessAddress"
                value="false"
                checked={formData.isDifferentBusinessAddress === false}
                onChange={() => handleBusinessAddressToggle(false)}
                disabled={isReadOnly}
              />
              <span>No</span>
            </label>
          </div>
        </FormField>

        {formData.isDifferentBusinessAddress && (
          <div className="business-address-fields">
            <h4>Business Address</h4>

            <div className="form-grid">
              <FormField
                label="Address"
                error={errors.businessAddress}
                className="grid-span-2"
              >
                <input
                  type="text"
                  className={`form-input ${errors.businessAddress ? 'error' : ''}`}
                  value={formData.businessAddress || ''}
                  onChange={(e) => handleChange('businessAddress', e.target.value)}
                  placeholder="Business street address"
                  disabled={isReadOnly}
                />
              </FormField>

              <FormField
                label="City"
                error={errors.businessCity}
              >
                <input
                  type="text"
                  className={`form-input ${errors.businessCity ? 'error' : ''}`}
                  value={formData.businessCity || ''}
                  onChange={(e) => handleChange('businessCity', e.target.value)}
                  placeholder="City"
                  disabled={isReadOnly}
                />
              </FormField>

              <FormField
                label="Postcode"
                error={errors.businessPostcode}
              >
                <input
                  type="text"
                  className={`form-input ${errors.businessPostcode ? 'error' : ''}`}
                  value={formData.businessPostcode || ''}
                  onChange={(e) => handleChange('businessPostcode', e.target.value)}
                  placeholder="Postcode"
                  disabled={isReadOnly}
                />
              </FormField>

              <FormField
                label="Country"
                error={errors.businessCountry}
                className="grid-span-2"
              >
                <CountrySelect
                  value={formData.businessCountry || ''}
                  onChange={(value) => handleChange('businessCountry', value)}
                  error={!!errors.businessCountry}
                  disabled={isReadOnly}
                />
              </FormField>
            </div>
          </div>
        )}
      </div>

      {!isReadOnly && (
        <div className="section-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              'Save Section'
            )}
          </button>

          {hasChanges && (
            <span className="unsaved-indicator">
              You have unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyDetails;
